/**
 * Permission Data Store
 * 
 * Centralized permission caching and management service with:
 * - TTL-based cache invalidation
 * - localStorage persistence
 * - Observable pattern for UI updates
 * - Multi-context support
 */

import type { ApiPermission } from '../types';
import apiService from './apiService';

/**
 * Cached permissions with metadata
 */
interface CachedPermissions {
  permissions: ApiPermission[];
  timestamp: number;
  ttl: number; // milliseconds
  context: string;
  role: string;
}

/**
 * Permission refresh event
 */
interface PermissionRefreshEvent {
  context: string;
  role: string;
  permissions: ApiPermission[];
  source: 'background' | '403-error' | 'manual' | 'page-refresh';
}

type PermissionSubscriber = (event: PermissionRefreshEvent) => void;

/**
 * Permission Data Store Service
 */
export class PermissionDataStore {
  private store: Map<string, CachedPermissions> = new Map();
  private subscribers: Set<PermissionSubscriber> = new Set();
  
  // Cache configuration
  private readonly DEFAULT_TTL = 15 * 60 * 1000; // 15 minutes
  private readonly STORAGE_KEY = 'oneportal_permissions_cache';
  
  // Statistics
  private stats = {
    hits: 0,
    misses: 0,
    refreshes: 0,
    lastRefresh: null as Date | null,
  };

  constructor() {
    this.loadFromStorage();
  }

  /**
   * Generate cache key from context and role
   */
  private getCacheKey(context: string, role: string): string {
    return `${context}:${role}`;
  }

  /**
   * Get permissions from cache
   */
  get(context: string, role: string): ApiPermission[] | null {
    const key = this.getCacheKey(context, role);
    const cached = this.store.get(key);

    if (!cached) {
      this.stats.misses++;
      console.log(`📊 Cache MISS: ${key}`);
      return null;
    }

    // Check if stale
    if (this.isStale(context, role)) {
      this.stats.misses++;
      console.log(`📊 Cache STALE: ${key}`);
      return null;
    }

    this.stats.hits++;
    console.log(`📊 Cache HIT: ${key}`);
    return cached.permissions;
  }

  /**
   * Peek at cached permissions without incrementing stats
   * Used for display/debugging purposes only
   */
  peek(context: string, role: string): ApiPermission[] | null {
    const key = this.getCacheKey(context, role);
    const cached = this.store.get(key);

    if (!cached) {
      return null;
    }

    // Check if stale
    if (this.isStale(context, role)) {
      return null;
    }

    return cached.permissions;
  }

  /**
   * Set permissions in cache
   */
  set(context: string, role: string, permissions: ApiPermission[], ttl?: number): void {
    const key = this.getCacheKey(context, role);
    
    const cached: CachedPermissions = {
      permissions,
      timestamp: Date.now(),
      ttl: ttl || this.DEFAULT_TTL,
      context,
      role,
    };

    this.store.set(key, cached);
    this.saveToStorage();
    
    console.log(`💾 Cached permissions for ${key} (TTL: ${cached.ttl}ms)`);
  }

  /**
   * Check if cached permissions are stale
   */
  isStale(context: string, role: string): boolean {
    const key = this.getCacheKey(context, role);
    const cached = this.store.get(key);

    if (!cached) {
      return true;
    }

    const age = Date.now() - cached.timestamp;
    return age > cached.ttl;
  }

  /**
   * Fetch permissions from API and cache
   */
  async fetch(context: string, role: string): Promise<ApiPermission[]> {
    console.log(`🔄 Fetching permissions for context: ${context}, role: ${role}`);
    
    try {
      // Map context to API format ("CUSTOMER" or "PARTNER")
      const apiContext = context === 'CONSUMER' ? 'CUSTOMER' : context;
      
      const response = await apiService.getRolePermissions(apiContext);
      
      if (!response.data?.roles) {
        throw new Error('Invalid permissions response');
      }

      // Find permissions for this role
      const roleData = response.data.roles.find(r => r.roleName === role);
      
      if (!roleData) {
        console.warn(`⚠️ No permissions found for role: ${role}`);
        return [];
      }

      const permissions = roleData.permissions;
      
      // Cache the permissions
      this.set(context, role, permissions);
      
      this.stats.refreshes++;
      this.stats.lastRefresh = new Date();
      
      console.log(`✅ Fetched and cached ${permissions.length} permission entries for ${role}`);
      
      return permissions;
    } catch (error) {
      console.error(`❌ Failed to fetch permissions:`, error);
      throw error;
    }
  }

  /**
   * Refresh permissions (fetch and update cache)
   */
  async refresh(context: string, role: string, source: PermissionRefreshEvent['source'] = 'manual'): Promise<ApiPermission[]> {
    console.log(`🔄 Refreshing permissions (source: ${source})`);
    
    const permissions = await this.fetch(context, role);
    
    // Notify subscribers
    this.notifySubscribers({
      context,
      role,
      permissions,
      source,
    });
    
    return permissions;
  }

  /**
   * Clear cache for specific context or all
   */
  clear(context?: string): void {
    if (context) {
      // Clear specific context
      const keys = Array.from(this.store.keys()).filter(key => key.startsWith(`${context}:`));
      keys.forEach(key => this.store.delete(key));
      console.log(`🗑️ Cleared cache for context: ${context}`);
    } else {
      // Clear all
      this.store.clear();
      console.log(`🗑️ Cleared all cached permissions`);
    }
    
    this.saveToStorage();
  }

  /**
   * Subscribe to permission refresh events
   */
  subscribe(callback: PermissionSubscriber): () => void {
    this.subscribers.add(callback);
    
    // Return unsubscribe function
    return () => {
      this.subscribers.delete(callback);
    };
  }

  /**
   * Notify all subscribers of permission change
   */
  private notifySubscribers(event: PermissionRefreshEvent): void {
    console.log(`📢 Notifying ${this.subscribers.size} subscribers of permission refresh`);
    this.subscribers.forEach(callback => {
      try {
        callback(event);
      } catch (error) {
        console.error('Error in permission subscriber:', error);
      }
    });
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return {
      ...this.stats,
      cacheSize: this.store.size,
      entries: Array.from(this.store.keys()),
    };
  }

  /**
   * Get TTL remaining for cached permissions
   */
  getTTLRemaining(context: string, role: string): number | null {
    const key = this.getCacheKey(context, role);
    const cached = this.store.get(key);

    if (!cached) {
      return null;
    }

    const age = Date.now() - cached.timestamp;
    const remaining = cached.ttl - age;
    
    return Math.max(0, remaining);
  }

  /**
   * Save cache to localStorage
   */
  private saveToStorage(): void {
    try {
      const serialized = JSON.stringify(Array.from(this.store.entries()));
      localStorage.setItem(this.STORAGE_KEY, serialized);
      console.log(`💾 Saved ${this.store.size} cache entries to localStorage`);
    } catch (error) {
      console.error('Failed to save permissions cache to localStorage:', error);
    }
  }

  /**
   * Load cache from localStorage
   */
  private loadFromStorage(): void {
    try {
      const serialized = localStorage.getItem(this.STORAGE_KEY);
      
      if (!serialized) {
        console.log('📂 No cached permissions found in localStorage');
        return;
      }

      const entries: [string, CachedPermissions][] = JSON.parse(serialized);
      this.store = new Map(entries);
      
      // Remove stale entries
      let removed = 0;
      for (const [key, cached] of this.store.entries()) {
        const age = Date.now() - cached.timestamp;
        if (age > cached.ttl) {
          this.store.delete(key);
          removed++;
        }
      }
      
      console.log(`📂 Loaded ${this.store.size} cache entries from localStorage (removed ${removed} stale)`);
      
      // Save cleaned cache
      if (removed > 0) {
        this.saveToStorage();
      }
    } catch (error) {
      console.error('Failed to load permissions cache from localStorage:', error);
      this.store.clear();
    }
  }

  /**
   * Get all cached contexts for debugging
   */
  getAllCached(): Array<{ context: string; role: string; timestamp: Date; ttlRemaining: number }> {
    return Array.from(this.store.values()).map(cached => ({
      context: cached.context,
      role: cached.role,
      timestamp: new Date(cached.timestamp),
      ttlRemaining: cached.ttl - (Date.now() - cached.timestamp),
    }));
  }
}

// Export singleton instance
export const permissionDataStore = new PermissionDataStore();
export default permissionDataStore;
